import type {AuthResponseInterface} from '@interfaces/auth'
import type {FC, ReactElement, ReactNode} from 'react'
import {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {ReactReduxContext} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import type {TeamsReauthRequiredDetail} from '@apis/helpers'
import {
  handleUnauthorized,
  reauthorizeIfTokenExpiring,
  TEAMS_REAUTH_REQUIRED_EVENT,
} from '@apis/helpers'
import type {RootState} from '@containers/App/store'
import {selectHasGuardedUnsavedChanges} from '@redux/selectors/customDashboardSelectors'
import {requestForceLogin} from '@shared/utils/buildAuthorizeUrl'
import {revokeProviderTokens} from '@shared/utils/revokeTokens'
import {isRunningInTeams} from '@shared/utils/teamsRuntime'
import {clearTokenExpiry, storeTokenExpiry} from '@shared/utils/tokenExpiry'

/**
 * Window events the new experience (src/v2) sends to this app's session: a
 * shared contract with src/v2/core/platform/auth.ts, not shared code.
 */
const LOGOUT_REQUESTED_EVENT = 'mbai:logout-requested'
const REAUTHORIZE_CHECK_EVENT = 'mbai:reauthorize-check'
const UNAUTHORIZED_EVENT = 'mbai:unauthorized'

/** What the new experience sends with `mbai:unauthorized`. */
type V2UnauthorizedDetail = {requestToken: string | null; respond: (retry: boolean) => void}

interface UserProviderProps {
  children?: ReactNode
}

export interface AuthContextProps {
  userIsLoggedIn: boolean
  loginUser: (creds: AuthResponseInterface | string) => void
  logoutUser: () => void
}

const AuthContext = createContext<AuthContextProps | null>(null)

const useAuthContext = (): AuthContextProps | null => useContext(AuthContext)

const clearAuthStorage = (): void => {
  localStorage.removeItem('token')
  // Leftover from before refresh tokens were dropped (AB#6617).
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('expires_in')
  localStorage.removeItem('username')
  clearTokenExpiry()
}

const LegacyAuthProvider: FC<UserProviderProps> = ({children}): ReactElement => {
  const navigate = useNavigate()
  const [userIsLoggedIn, setUserIsLoggedIn] = useState<boolean>(!!localStorage.getItem('token'))

  const logoutUser = useCallback(async (): Promise<void> => {
    // An explicit logout must end on the login form even if the revoke below
    // times out and the auth site's session survives: AuthRedirect consumes
    // this and asks for prompt=login (AB#6617). Set before the state change
    // below can mount AuthRedirect. Not in Teams, which has no AuthRedirect.
    if (!isRunningInTeams()) {
      requestForceLogin()
    }
    // Revoke on the auth provider before dropping the tokens locally, so a
    // copied bearer token stops working at logout (AB#6319). Bounded to
    // REVOKE_TIMEOUT_MS and never throws; storage is cleared either way.
    await revokeProviderTokens()
    clearAuthStorage()
    setUserIsLoggedIn(false)

    // Inside the Teams tab, navigate('/login') mounts AuthRedirect, which
    // immediately tries a top-level redirect to the auth host -- Teams
    // refuses to frame it (X-Frame-Options: deny), stranding the tab on a
    // blank/error page instead of the sign-in screen. TeamsApp already
    // owns exactly this situation for a 401 in the Teams tab (see
    // apis/helpers.ts); reuse the same event instead of
    // duplicating a second Teams-aware logout path. Marked as the user's
    // own logout, so TeamsApp doesn't sign straight back in (AB#6617).
    if (isRunningInTeams()) {
      window.dispatchEvent(
        new CustomEvent<TeamsReauthRequiredDetail>(TEAMS_REAUTH_REQUIRED_EVENT, {
          detail: {explicitLogout: true},
        }),
      )
      return
    }

    navigate('/login')
  }, [navigate])

  // The new experience asks for a logout through this event, so both use this one.
  useEffect(() => {
    const onLogoutRequested = (): void => void logoutUser()
    window.addEventListener(LOGOUT_REQUESTED_EVENT, onLogoutRequested)
    return () => window.removeEventListener(LOGOUT_REQUESTED_EVENT, onLogoutRequested)
  }, [logoutUser])

  // The new experience's shell asks for the proactive re-authorization check
  // on start and on every page change, as useProactiveReauthorize does for the
  // classic shell. Unsaved dashboard edits hold it off, as in the classic shell
  // (the v1 store, when there is one).
  const redux = useContext(ReactReduxContext)
  useEffect(() => {
    const onCheck = (): void =>
      void reauthorizeIfTokenExpiring({
        hasUnsavedChanges: () =>
          redux ? selectHasGuardedUnsavedChanges(redux.store.getState() as RootState) : false,
      })
    window.addEventListener(REAUTHORIZE_CHECK_EVENT, onCheck)
    return () => window.removeEventListener(REAUTHORIZE_CHECK_EVENT, onCheck)
  }, [redux])

  const loginUser = useCallback(
    (creds: AuthResponseInterface | string): void => {
      const token = typeof creds === 'string' ? creds : creds.token
      const expires_in = typeof creds !== 'string' ? creds.expires_in : null

      // No refresh token is stored, even if the response carries one: the UI
      // app gets none from the auth service (AB#6615) and never refreshes;
      // an expired token goes through the silent re-authorize (AB#6617).
      localStorage.setItem('token', token)
      if (expires_in) {
        localStorage.setItem('expires_in', expires_in.toString())
      }
      // Absolute expiry for the proactive re-authorize (AB#6617).
      storeTokenExpiry(expires_in)

      setUserIsLoggedIn(true)
      navigate('/')
    },
    [navigate],
  )

  const contextValue = useMemo(
    () => ({
      userIsLoggedIn,
      loginUser,
      logoutUser,
    }),
    [userIsLoggedIn, loginUser, logoutUser],
  )

  // The new experience's requests that run into a 401 take this app's own path.
  useEffect(() => {
    const onUnauthorized = (event: Event): void => {
      const {requestToken, respond} = (event as CustomEvent<V2UnauthorizedDetail>).detail
      void handleUnauthorized({authContext: contextValue, requestToken}).then(respond, () =>
        respond(false),
      )
    }
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
  }, [contextValue])

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

const AuthProvider: FC<UserProviderProps> = ({children}): ReactElement => (
  <LegacyAuthProvider>{children}</LegacyAuthProvider>
)

export {AuthProvider, useAuthContext}
